import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Collapse, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { ChannelType } from '../../../../../services/useChannels';
import {
  ChannelCard,
  ChannelCardHeader,
  ChannelCardContent,
  ChannelInfo,
  ChannelIcon,
  StatusChip,
  ExpandButton,
  UnitsList,
  TableHeader,
  TableHeaderCell,
} from '../../../styles/UnitsMappingStyles';
import { ChannelConfig, UnitData, ChannelName } from '../../../types';
import FilterSection from '../FilterSection';
import UnitRowItem from '../UnitRowItem';

// Channel logo SVGs
import airbnbLogo from '@assets/svg/AirbnbLogo.svg';
import bookingLogo from '@assets/svg/BookingLogo.svg';
import gathernLogo from '@assets/svg/GathernIcon2.svg';

// Helper function to render channel logo with rounded style
const renderChannelLogo = (channelType: ChannelType) => {
  const logoStyle = { 
    width: '24px', 
    height: '24px', 
    borderRadius: '50%',
    objectFit: 'cover' as const,
  };
  
  switch (channelType) {
    case ChannelType.Airbnb:
      return <img src={airbnbLogo} alt="Airbnb" style={logoStyle} />;
    case ChannelType.Booking:
      return <img src={bookingLogo} alt="Booking" style={logoStyle} />;
    case ChannelType.Gathern:
      return <img src={gathernLogo} alt="Gathern" style={logoStyle} />;
    default:
      return null;
  }
};

interface ChannelCardComponentProps {
  channelKey: string;
  config: ChannelConfig;
  units: UnitData[];
  filteredUnits: UnitData[];
  isExpanded: boolean;
  isLoading: boolean;
  searchQuery: string;
  statusFilter: string;
  selectedCount: number;
  onToggleExpand: (channelKey: string) => void;
  onSearchChange: (channelKey: string, query: string) => void;
  onStatusFilterChange: (channelKey: string, status: string) => void;
  onUnitToggle: (unit: UnitData, channelKey: ChannelName) => void;
  isUnitSelected: (unitId: number, channel: ChannelName) => boolean;
}

const ChannelCardComponent: React.FC<ChannelCardComponentProps> = memo(({
  channelKey,
  config,
  units,
  filteredUnits,
  isExpanded,
  isLoading,
  searchQuery,
  statusFilter,
  selectedCount,
  onToggleExpand,
  onSearchChange,
  onStatusFilterChange,
  onUnitToggle,
  isUnitSelected,
}) => {
  const { t } = useTranslation();

  const handleSearchChange = useCallback((query: string) => {
    onSearchChange(channelKey, query);
  }, [channelKey, onSearchChange]);

  const handleStatusFilterChange = useCallback((status: string) => {
    onStatusFilterChange(channelKey, status);
  }, [channelKey, onStatusFilterChange]);

  const handleToggleExpand = useCallback(() => {
    onToggleExpand(channelKey);
  }, [channelKey, onToggleExpand]);

  return (
    <ChannelCard>
      <ChannelCardHeader onClick={handleToggleExpand}>
        <ChannelInfo>
          <ChannelIcon sx={{ bgcolor: 'transparent', padding: '0' }}>
            {renderChannelLogo(config.channelType)}
          </ChannelIcon>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
            {config.name} ({units.length})
          </Typography>
          {selectedCount > 0 && (
            <StatusChip
              label={`${t('CHANNELS.UNITS_MAPPING.SELECTED', 'تم الاختيار')} (${selectedCount})`}
              size="small"
              icon={<CheckIcon sx={{ fontSize: 14 }} />}
            />
          )}
        </ChannelInfo>
        <ExpandButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ExpandButton>
      </ChannelCardHeader>

      <Collapse in={isExpanded}>
        <ChannelCardContent>
          <FilterSection
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
          />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress size={24} sx={{ color: '#5b3fff' }} />
            </Box>
          ) : filteredUnits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Typography sx={{ color: '#666', fontFamily: '"IBM Plex Sans Arabic", sans-serif' }}>
                {t('CHANNELS.UNITS_MAPPING.NO_UNITS', 'لا توجد وحدات متاحة')}
              </Typography>
            </Box>
          ) : (
            <UnitsList>
              <TableHeader>
                <Box sx={{ width: '40px' }} />
                <TableHeaderCell sx={{ flex: 1 }}>{t('CHANNELS.UNITS_MAPPING.PROPERTY', 'العقار')}</TableHeaderCell>
                <TableHeaderCell sx={{ width: '120px' }}>{t('CHANNELS.TABLE_ID', 'الرقم التعريفي')}</TableHeaderCell>
                <TableHeaderCell sx={{ width: '100px' }}>{t('CHANNELS.TABLE_STATUS', 'الحالة')}</TableHeaderCell>
              </TableHeader>
              {filteredUnits.map((unit) => {
                const isSelected = isUnitSelected(unit.id, channelKey as ChannelName);
                const isMapped = unit.isMapped === true;

                return (
                  <UnitRowItem
                    key={unit.id}
                    unit={unit}
                    channelKey={channelKey as ChannelName}
                    isSelected={isSelected}
                    isMapped={isMapped}
                    onToggle={onUnitToggle}
                  />
                );
              })}
            </UnitsList>
          )}
        </ChannelCardContent>
      </Collapse>
    </ChannelCard>
  );
});

ChannelCardComponent.displayName = 'ChannelCardComponent';

export default ChannelCardComponent;
